/**
 * useVoiceRecorder
 *
 * Hook around `MediaRecorder` + the `transcribe` Edge Function that
 * exposes a 4-state flow for chat-style voice input:
 *
 *   idle → recording → transcribing → idle (with transcript delivered)
 *
 * Error cases (mic permission denied, STT failure) surface a toast and
 * return to `idle`. The hook itself stores no transcript — it hands the
 * result to `onTranscript` so the caller can decide what to do with it
 * (append to an input field, replace it, etc.).
 */

import { useCallback, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type VoiceRecorderState = 'idle' | 'recording' | 'transcribing';

export type UseVoiceRecorderOptions = {
  /** Called with the final transcript once STT completes. */
  onTranscript: (transcript: string) => void;
};

export type UseVoiceRecorderReturn = {
  state: VoiceRecorderState;
  start: () => Promise<void>;
  stop: () => void;
};

export function useVoiceRecorder({
  onTranscript,
}: UseVoiceRecorderOptions): UseVoiceRecorderReturn {
  const [state, setState] = useState<VoiceRecorderState>('idle');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setState('transcribing');
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        try {
          const formData = new FormData();
          formData.append('audio', audioBlob, 'recording.webm');
          formData.append('context', 'voice_note');
          formData.append('language', 'de');
          const { data, error } = await supabase.functions.invoke('transcribe', {
            body: formData,
          });
          if (error) throw new Error(error.message || 'STT-Fehler');
          const transcript: string = data?.text ?? '';
          if (transcript.trim()) onTranscript(transcript.trim());
        } catch (err) {
          toast.error('Transkription fehlgeschlagen', {
            description: err instanceof Error ? err.message : 'Unbekannter Fehler',
          });
        } finally {
          setState('idle');
        }
      };

      mediaRecorder.start();
      setState('recording');
    } catch {
      toast.error('Mikrofon-Zugriff verweigert', {
        description: 'Bitte Mikrofon-Berechtigung im Browser erlauben.',
      });
      setState('idle');
    }
  }, [onTranscript]);

  const stop = useCallback(() => {
    mediaRecorderRef.current?.stop();
  }, []);

  return { state, start, stop };
}
